import { matchHudGeometry } from "../ui/match-hud";
import { resultRects } from "../ui/results";
import { koTimeline } from "../ui/ko";
import { ResultsStage } from "./results-stage";
import { HitParticles } from "./particles";
import { ProjectileVisuals } from "./projectiles";
import * as THREE from "three";
import { PLAYER_Z, OPPONENT_Z } from "../game/world";
import type { Backyard } from "./backyard";
import theme from "../../assets/theme.json";
import themeV3Json from "../../assets/theme-v3.json";
import type { GameState } from "../game";
import type { RuntimePlatform, SafeInsets } from "../platform/types";
import {
  computeGameViewport,
  computeLayoutRects,
  DESIGN_HEIGHT,
  DESIGN_WIDTH,
} from "../ui/layout";
import type { LayoutRects } from "../ui/layout";
import { CANVAS_FONT_FAMILY } from "./fonts";
import {
  loadArena,
  loadArtSnowball,
  loadArtSnowman,
  loadPodium,
} from "./art-loader";
import { drawArt, drawNineSlice, preloadUiArt } from "./ui-art";

const themeV3 = themeV3Json as {
  sky: { top: string };
  teams: { player: { base: string }; opponent: { base: string } };
  lights: { hemiSky: string; hemiGround: string; fog: string };
};

export const DEBUG_OVERLAY_DESIGN_RECT = {
  x: 94,
  y: 165,
  width: 172,
  height: 66,
} as const;

export function drawCurrencyCounts(
  context: CanvasRenderingContext2D,
  profile = { coins: 0, gems: 0 },
): void {
  context.fillStyle = "#1B1F4A";
  context.font = `600 14px ${CANVAS_FONT_FAMILY}`;
  context.textAlign = "left";
  context.fillText(String(profile.coins), 44, 32);
  context.fillText(String(profile.gems), 314, 32);
}

export function cameraShakeOffset(
  elapsedMs: number,
  reduceMotion: boolean,
): number {
  return reduceMotion || elapsedMs < 0 || elapsedMs >= 120
    ? 0
    : 6 * (1 - elapsedMs / 120) * Math.cos(elapsedMs * 0.15);
}

export function verticalFov(width: number, height: number): number {
  const aspect = width / Math.max(1, height);
  const fov = (2 * Math.atan(Math.tan(Math.PI / 10) / aspect) * 180) / Math.PI;
  return aspect > 0.62 ? Math.max(fov, 50) : fov;
}

export function throwDolly(elapsedMs: number, reduceMotion: boolean): number {
  if (reduceMotion || elapsedMs < 0 || elapsedMs >= 440) return 0;
  if (elapsedMs < 140) return (0.25 * elapsedMs) / 140;
  const t = (elapsedMs - 140) / 300 - 1;
  return -0.25 * (2.2 * t ** 3 + 1.2 * t ** 2);
}

export const OPPONENT_SCALE = 0.86;

export const CAMERA_BEATS = [
  { position: [0, 4.4, 9.4], target: [0, 1.35, -1.5] },
  { position: [0.6, 4.8, 10.2], target: [0.2, 1.5, -2.2] },
  { position: [0, 5.3, 11], target: [0, 1.6, -3] },
] as const;

export function cameraBeat(round: number, elapsedMs: number) {
  const current = CAMERA_BEATS[Math.max(0, Math.min(2, round - 1))]!;
  const previous = CAMERA_BEATS[Math.max(0, Math.min(2, round - 2))]!;
  const t = Math.max(0, Math.min(1, elapsedMs / 600));
  const ease = t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2;
  return {
    position: current.position.map(
      (v, i) => previous.position[i]! + (v - previous.position[i]!) * ease,
    ),
    target: current.target.map(
      (v, i) => previous.target[i]! + (v - previous.target[i]!) * ease,
    ),
  };
}

export function configureMatchCamera(camera: THREE.PerspectiveCamera): void {
  camera.fov = verticalFov(camera.aspect, 1);
  camera.position.set(0, 5.3, 11);
  camera.lookAt(0, 1.6, -3);
  camera.updateMatrixWorld();
  camera.updateProjectionMatrix();
}

export function positionPlayer(root: THREE.Object3D): void {
  root.position.set(0, 0, PLAYER_Z);
  root.scale.setScalar(1);
  root.rotation.y = Math.PI;
}

export function projectPlayerBounds(
  root: THREE.Object3D,
  width: number,
  height: number,
) {
  const camera = new THREE.PerspectiveCamera(58, width / height, 0.1, 100);
  configureMatchCamera(camera);
  return projectBounds(root, camera, width, height);
}

export function projectBounds(
  root: THREE.Object3D,
  camera: THREE.Camera,
  width: number,
  height: number,
  maxInstances = Infinity,
  firstInstance = 0,
) {
  root.updateMatrixWorld(true);
  camera.updateMatrixWorld(true);
  // Measure the actual vertices without retaining a projected Vector3 for
  // each one. Results fitting calls this hundreds of times per frame.
  const point = new THREE.Vector3();
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity;
  root.traverseVisible((node) => {
    if (
      !(node instanceof THREE.Mesh) ||
      /outline|shadow/i.test(node.name) ||
      (root.name.endsWith("snowman") && /ball/i.test(node.name))
    )
      return;
    const mesh = node as THREE.Mesh<THREE.BufferGeometry>;
    const positions = mesh.geometry.getAttribute("position");
    const count =
      node instanceof THREE.InstancedMesh
        ? Math.min(node.count, maxInstances)
        : 1;
    for (let instance = firstInstance; instance < count; instance++) {
      const transform = mesh.matrixWorld.clone();
      if (node instanceof THREE.InstancedMesh) {
        const matrix = new THREE.Matrix4();
        node.getMatrixAt(instance, matrix);
        transform.multiply(matrix);
      }
      for (let i = 0; i < positions.count; i++) {
        point
          .fromBufferAttribute(positions, i)
          .applyMatrix4(transform)
          .project(camera);
        minX = Math.min(minX, point.x);
        maxX = Math.max(maxX, point.x);
        minY = Math.min(minY, point.y);
        maxY = Math.max(maxY, point.y);
      }
    }
  });
  return {
    x: ((minX + 1) / 2) * width,
    y: ((1 - maxY) / 2) * height,
    width: ((maxX - minX) / 2) * width,
    height: ((maxY - minY) / 2) * height,
  };
}

// Measure only the head mesh's front hemisphere, excluding its hat, scarf,
// outline and expression children. Expressions contain only eyes/mouth marks.
function projectHeadFrontBounds(
  head: THREE.Mesh,
  camera: THREE.Camera,
  width: number,
  height: number,
) {
  head.updateWorldMatrix(true, false);
  const positions = head.geometry.getAttribute("position");
  const point = new THREE.Vector3();
  const box = new THREE.Box2();
  for (let i = 0; i < positions.count; i++) {
    point.fromBufferAttribute(positions, i);
    if (point.z < 0) continue;
    point.applyMatrix4(head.matrixWorld).project(camera);
    box.expandByPoint(
      new THREE.Vector2(
        ((point.x + 1) * width) / 2,
        ((1 - point.y) * height) / 2,
      ),
    );
  }
  return {
    x: box.min.x,
    y: box.min.y,
    width: box.max.x - box.min.x,
    height: box.max.y - box.min.y,
  };
}

export function applyGameplayFacing(
  playerRoot: THREE.Object3D,
  opponentRoot: THREE.Object3D,
): void {
  playerRoot.rotation.y = Math.PI;
  opponentRoot.rotation.y = 0;
}

export class GameRenderer {
  readonly #renderer: THREE.WebGLRenderer;
  readonly #scene = new THREE.Scene();
  readonly #camera = new THREE.PerspectiveCamera(
    35,
    DESIGN_WIDTH / DESIGN_HEIGHT,
    0.1,
    100,
  );
  readonly #uiScene = new THREE.Scene();
  readonly #uiCamera = new THREE.OrthographicCamera(
    0,
    DESIGN_WIDTH,
    DESIGN_HEIGHT,
    0,
    -1,
    1,
  );
  readonly #uiContext: CanvasRenderingContext2D;
  readonly #uiTexture: THREE.CanvasTexture;
  #playerSnowman = { root: new THREE.Group(), head: new THREE.Object3D() };
  #opponentSnowman = { root: new THREE.Group(), head: new THREE.Object3D() };
  #arena: Backyard | undefined;
  #stage: ResultsStage | undefined;
  #lastKind = "";
  #coinBounds = { x: 0, y: 0, width: 0, height: 32 };
  #lastMatchTime = 0;
  #captureBackground = false;
  #lastDrawSignature: string | null = null;
  #sky: THREE.Texture | undefined;
  #resultBackground: THREE.CanvasTexture | undefined;
  readonly #canvas: HTMLCanvasElement;
  #menuSnowball = new THREE.Group();
  readonly #projectiles = new ProjectileVisuals();
  readonly #hitParticles = new HitParticles();
  readonly ready: Promise<void>;
  readonly #debug: boolean;
  #qaHidden = false;
  #drawCalls = 0;
  readonly #worldCaptures: Array<(png: string) => void> = [];
  #reduceMotion = false;
  #triangles = 0;
  #width = DESIGN_WIDTH;
  #height = DESIGN_HEIGHT;
  #safe: SafeInsets = { top: 0, bottom: 0, left: 0, right: 0 };
  #viewport = computeGameViewport(DESIGN_WIDTH, DESIGN_HEIGHT, {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });
  #layoutRects: LayoutRects = computeLayoutRects(
    this.#viewport,
    { top: 0, right: 0, bottom: 0, left: 0 },
    DESIGN_WIDTH,
    DESIGN_HEIGHT,
  );

  constructor(platform: RuntimePlatform) {
    this.#canvas = platform.canvas;
    this.#renderer = new THREE.WebGLRenderer({
      canvas: platform.canvas,
      context: platform.context,
      antialias: true,
    });
    this.#renderer.autoClear = false;
    this.#renderer.info.autoReset = false;

    const skyTop = themeV3.sky.top;
    this.#renderer.setClearColor(parseInt(skyTop.replace("#", "0x")));
    this.#debug = platform.debug;
    this.#reduceMotion = platform.reduceMotion ?? false;

    configureMatchCamera(this.#camera);

    // Lighting (v3 daylight)
    const keyLight = new THREE.DirectionalLight(0xfff4e0, 2.4);
    keyLight.position.set(-2, 3, 4);
    this.#scene.add(keyLight);

    const hemiSky = themeV3.lights.hemiSky;
    const hemiGround = themeV3.lights.hemiGround;
    const hemiLight = new THREE.HemisphereLight(
      parseInt(hemiSky.replace("#", "0x")),
      parseInt(hemiGround.replace("#", "0x")),
      1.0,
    );
    this.#scene.add(hemiLight);
    const backLight = new THREE.DirectionalLight(0xffc79a, 1.1);
    backLight.position.set(-4, 3, -6);
    this.#scene.add(backLight);
    const skyData = new Uint8Array(256 * 4);
    const top = new THREE.Color(0x6fa4ff),
      horizon = new THREE.Color(0xffd6e6);
    for (let i = 0; i < 256; i++) {
      const c = horizon
        .clone()
        .lerp(top, i / 255)
        .convertLinearToSRGB();
      skyData.set([c.r * 255, c.g * 255, c.b * 255, 255], i * 4);
    }
    const sky = new THREE.DataTexture(skyData, 1, 256);
    sky.colorSpace = THREE.SRGBColorSpace;
    sky.needsUpdate = true;
    this.#scene.background = sky;
    this.#sky = sky;

    // Fog (linear, lavender)
    this.#scene.fog = new THREE.Fog(0xffd6e6, 22, 70);

    platform.uiCanvas.width = DESIGN_WIDTH * 2;
    platform.uiCanvas.height = DESIGN_HEIGHT * 2;
    const uiContext = platform.uiCanvas.getContext("2d");
    if (!uiContext)
      throw new Error("A 2D canvas is required for the canvas-only UI");
    this.#uiContext = uiContext;
    this.#uiContext.scale(2, 2);
    this.#uiTexture = new THREE.CanvasTexture(platform.uiCanvas);
    this.#uiTexture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.MeshBasicMaterial({
      map: this.#uiTexture,
      transparent: true,
    });
    const ui = new THREE.Mesh(
      new THREE.PlaneGeometry(DESIGN_WIDTH, DESIGN_HEIGHT),
      material,
    );
    ui.position.set(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, 0);
    this.#uiScene.add(ui);
    this.resize(
      platform.width,
      platform.height,
      platform.pixelRatio,
      platform.safeInsets,
    );
    this.ready = Promise.all([preloadUiArt(), this.#loadArtScene()]).then(
      () => undefined,
    );
  }

  async #loadArtScene(): Promise<void> {
    const [player, opponent, arena, menuBall, podium] = await Promise.all([
      loadArtSnowman("player"),
      loadArtSnowman("opponent"),
      loadArena(),
      loadArtSnowball("M"),
      loadPodium(),
    ]);
    this.#arena = arena;
    this.#stage = new ResultsStage(player.root, opponent.root, podium);
    this.#scene.add(this.#stage);
    positionPlayer(player.root);
    opponent.root.position.set(0, 0, OPPONENT_Z);
    opponent.root.scale.setScalar(OPPONENT_SCALE);
    applyGameplayFacing(player.root, opponent.root);
    menuBall.position.set(0, 1.5, 0);
    menuBall.visible = false;
    this.#scene.remove(
      this.#playerSnowman.root,
      this.#opponentSnowman.root,
      this.#menuSnowball,
      this.#projectiles,
    );
    this.#playerSnowman = player;
    this.#opponentSnowman = opponent;
    this.#menuSnowball = menuBall;
    this.#scene.add(
      player.root,
      opponent.root,
      menuBall,
      arena,
      this.#projectiles,
      this.#hitParticles.mesh,
    );
  }

  resize(
    width: number,
    height: number,
    pixelRatio: number,
    safeInsets: SafeInsets,
  ): void {
    this.#lastDrawSignature = null;
    this.#renderer.setPixelRatio(Math.min(pixelRatio, 2));
    this.#renderer.setSize(width, height, false);
    this.#width = width;
    this.#height = height;
    this.#safe = safeInsets;
    this.#camera.aspect = width / Math.max(1, height);
    this.#camera.fov = verticalFov(width, height);
    this.#camera.updateProjectionMatrix();
    this.#viewport = computeGameViewport(width, height, safeInsets);
    this.#layoutRects = computeLayoutRects(
      this.#viewport,
      safeInsets,
      width,
      height,
    );
  }

  worldIds(): string[] {
    return [
      "player",
      "opponent",
      "opponent.head",
      "ball:roll",
      "home.logo",
      "home.coins",
      "home.coinIcon",
      ...(this.#arena?.entities.keys() ?? []),
      ...this.#projectiles.entities.keys(),
    ];
  }
  hideAll(hidden = true): void {
    this.#qaHidden = hidden;
    this.#scene.visible = !hidden;
  }
  particleCount(kind: string): number {
    return kind === "confetti"
      ? (this.#stage?.visibleConfetti ?? 0)
      : kind === "snow"
        ? (this.#arena?.snow.count ?? 0)
        : kind === "spark"
          ? this.#hitParticles.mesh.count
          : 0;
  }
  facingDot(role: "hero" | "side"): number | null {
    if (this.#lastKind !== "results" || !this.#stage?.visible) return null;
    const root =
      role === "hero"
        ? this.#stage.hero
        : this.#stage.hero === this.#stage.player
          ? this.#stage.opponent
          : this.#stage.player;
    let face: THREE.Object3D | undefined;
    root.traverseVisible((node) => {
      if (node.name.startsWith("face_")) face = node;
    });
    if (!face) return null;
    face.updateWorldMatrix(true, false);
    const normal = new THREE.Vector3(0, 0, 1)
      .applyNormalMatrix(new THREE.Matrix3().getNormalMatrix(face.matrixWorld))
      .normalize();
    const view = this.#camera.position
      .clone()
      .sub(face.getWorldPosition(new THREE.Vector3()))
      .normalize();
    return normal.dot(view);
  }
  getBounds(id: string) {
    if (this.#qaHidden) return null;
    if (this.#lastKind === "results" && this.#arena?.entities.has(id))
      return null;
    if (id === "home.coins")
      return this.#lastKind === "menu" ? this.#coinBounds : null;
    if (id === "home.coinIcon")
      return this.#lastKind === "menu"
        ? {
            x: this.#coinBounds.x + 14,
            y: this.#coinBounds.y + 4,
            width: 24,
            height: 24,
          }
        : null;
    if (id === "home.logo") {
      const w = Math.min(this.#width * 0.72, (this.#height * 0.28 * 194) / 87);
      return this.#lastKind === "menu"
        ? {
            x: (this.#width - w) / 2,
            y: Math.min(
              this.#safe.top + 90,
              this.#layoutRects.play.y - 16 - (w * 87) / 194,
            ),
            width: w,
            height: (w * 87) / 194,
          }
        : null;
    }

    if (id === "opponent.head") {
      if (!this.#opponentSnowman.root.visible) return null;
      return projectBounds(
        this.#opponentSnowman.head,
        this.#camera,
        this.#width,
        this.#height,
      );
    }

    if (id === "results.heroBody") {
      const body = this.#stage?.hero.getObjectByName("body");
      return this.#lastKind === "results" && body
        ? projectBounds(body, this.#camera, this.#width, this.#height)
        : null;
    }
    if (id === "results.sideFace" || id === "results.sideBody") {
      const root =
        this.#stage?.hero === this.#playerSnowman.root
          ? this.#opponentSnowman.root
          : this.#playerSnowman.root;
      if (id === "results.sideFace" && this.#width > this.#height) {
        const head = root.getObjectByName("head");
        return this.#lastKind === "results" && head instanceof THREE.Mesh
          ? projectHeadFrontBounds(
              head as THREE.Mesh<THREE.BufferGeometry>,
              this.#camera,
              this.#width,
              this.#height,
            )
          : null;
      }
      let part: THREE.Object3D | undefined;
      root.traverseVisible((n) => {
        if (
          id.endsWith("Body") ? n.name === "body" : n.name.startsWith("face_")
        )
          part = n;
      });
      return this.#lastKind === "results" && part
        ? projectBounds(part, this.#camera, this.#width, this.#height)
        : null;
    }
    if (id === "results.sideHead") {
      const model =
        this.#stage?.hero === this.#playerSnowman.root
          ? this.#opponentSnowman
          : this.#playerSnowman;
      return this.#lastKind === "results"
        ? projectBounds(model.head, this.#camera, this.#width, this.#height)
        : null;
    }
    if (
      id === "results.sideHat" ||
      id === "results.dizzyStars" ||
      id === "results.dizzyStar" ||
      /^results\.dizzyStar\.[012]$/.test(id)
    ) {
      if (
        this.#lastKind !== "results" ||
        !this.#stage?.visible ||
        !this.#stage.stars.count
      )
        return null;
      const support =
        this.#stage.hero === this.#stage.player
          ? this.#stage.opponent
          : this.#stage.player;
      return projectBounds(
        id === "results.sideHat"
          ? support.getObjectByName("anchor_hat")!
          : this.#stage.stars,
        this.#camera,
        this.#width,
        this.#height,
        id === "results.dizzyStar"
          ? 1
          : /^results\.dizzyStar\.[012]$/.test(id)
            ? Number(id.at(-1)) + 1
            : 3,
        /^results\.dizzyStar\.[012]$/.test(id) ? Number(id.at(-1)) : 0,
      );
    }
    if (
      /^results\.(topFace|topEllipse|sideWall|heroBase|heroSurface|sideBase|sideSurface|sideShadow)$/.test(
        id,
      )
    ) {
      return this.#lastKind === "results" && this.#stage?.visible
        ? this.#stage.contactBounds(
            id.slice(8),
            this.#camera,
            this.#width,
            this.#height,
          )
        : null;
    }
    if (id === "results.podium") {
      return this.#lastKind === "results" && this.#stage?.visible
        ? projectBounds(
            this.#stage.podium,
            this.#camera,
            this.#width,
            this.#height,
          )
        : null;
    }
    if (id === "results.winner")
      id =
        this.#stage?.hero === this.#opponentSnowman.root
          ? "opponent"
          : "player";
    if (id === "results.opponent")
      id =
        this.#stage?.hero === this.#opponentSnowman.root
          ? "player"
          : "opponent";
    const root =
      id === "ball:roll"
        ? this.#menuSnowball
        : id === "player"
          ? this.#playerSnowman.root
          : id === "opponent"
            ? this.#opponentSnowman.root
            : (this.#arena?.entities.get(id) ??
              this.#projectiles.entities.get(id));
    if (!root || !root.visible) return null;
    return projectBounds(root, this.#camera, this.#width, this.#height);
  }

  getHeadPoint(who: "player" | "opponent") {
    const root =
      who === "player" ? this.#playerSnowman.root : this.#opponentSnowman.root;
    const p = new THREE.Vector3(0, 1.5325, 0)
      .applyMatrix4(root.matrixWorld)
      .project(this.#camera);
    return {
      x: ((p.x + 1) * this.#width) / 2,
      y: ((1 - p.y) * this.#height) / 2,
    };
  }

  captureWorld(): Promise<string> {
    return new Promise((resolve) => this.#worldCaptures.push(resolve));
  }

  getFraming() {
    this.#scene.updateMatrixWorld(true);
    const ray = new THREE.Raycaster(),
      plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0.035),
      hit = new THREE.Vector3();
    let ground = 0,
      clear = 0;
    for (let i = 0; i < 24; i++)
      for (let j = 0; j < 24; j++) {
        ray.setFromCamera(
          new THREE.Vector2((i + 0.5) / 12 - 1, (j + 0.5) / 12 - 1),
          this.#camera,
        );
        if (!ray.ray.intersectPlane(plane, hit)) continue;
        const distance = hit.distanceTo(this.#camera.position);
        if (
          distance < 70 &&
          Math.abs(hit.x) <= 60 &&
          hit.z >= -85 &&
          hit.z <= 35
        ) {
          ground++;
          if (distance < 46) clear++;
        }
      }
    const frustum = new THREE.Frustum().setFromProjectionMatrix(
      new THREE.Matrix4().multiplyMatrices(
        this.#camera.projectionMatrix,
        this.#camera.matrixWorldInverse,
      ),
    );
    const props = [...(this.#arena?.entities.values() ?? [])];
    return {
      viewport: { width: this.#width, height: this.#height },
      camera: {
        fov: this.#camera.fov,
        aspect: this.#camera.aspect,
        position: this.#camera.position.toArray(),
      },
      player: this.getBounds("player"),
      opponent: this.getBounds("opponent"),
      groundFraction: ground / 576,
      groundClearFraction: clear / 576,
      propsInView:
        this.#lastKind === "results"
          ? 0
          : props.filter((p) =>
              frustum.intersectsBox(new THREE.Box3().setFromObject(p)),
            ).length,
      propsTotal: props.length,
    };
  }

  knockoutFall(): number {
    return Math.max(
      Math.abs(this.#playerSnowman.root.rotation.z),
      Math.abs(this.#opponentSnowman.root.rotation.z),
    );
  }

  getRenderStats(): { drawCalls: number; triangles: number } {
    return { drawCalls: this.#drawCalls, triangles: this.#triangles };
  }

  render(
    time: number,
    state: Readonly<GameState>,
    phaseTime = 0,
    ending?: { t: number; ko: boolean; loser: "player" | "opponent" },
    draw = true,
    reuseFrame = false,
  ): void {
    // Show appropriate objects based on game state
    const isMenu = state.kind === "menu";
    const isMatch = state.kind === "match";
    const isResults = state.kind === "results";
    if (isMatch) this.#lastMatchTime = time;
    // Keep the specified scrim at 50% while meeting P3's measured world
    // luminance range. Grade only the world canvas, beneath the separate HUD.
    if (this.#canvas.style)
      this.#canvas.style.filter =
        isMatch && state.match.paused ? "brightness(0.80)" : "";
    if (this.#lastKind !== state.kind) {
      this.#captureBackground = isResults;
      this.#scene.background = this.#sky ?? null;
      if (!isResults) this.#stage?.restore();
      if (isMatch)
        for (const root of [
          this.#playerSnowman.root,
          this.#opponentSnowman.root,
        ])
          root.traverse((n) => {
            if (n.name.startsWith("face_"))
              n.visible = n.name === "face_determined";
          });
      this.#lastKind = state.kind;
    }
    // Manual QA ticks still update poses and measurement geometry, but only
    // present at RAF/capture boundaries. Submitting every intermediate tick
    // can saturate software WebGL's queue and starve RAF and screenshots.
    // Frozen/manual RAFs can retain identical pixels; captures always redraw.
    // Backdrop and world-image captures require their pixels synchronously.
    const capture = this.#captureBackground || this.#worldCaptures.length > 0;
    const signature =
      reuseFrame && (draw || capture)
        ? JSON.stringify([
            time,
            state,
            phaseTime,
            ending,
            this.#width,
            this.#height,
            this.#renderer.getPixelRatio(),
            this.#safe,
            this.#qaHidden,
          ])
        : null;
    // A retained frame includes its poses and measurement geometry. Skipping
    // only the GPU submission still runs the full results fit on every frozen
    // RAF, keeping the main thread busy while reload/resize events wait.
    if (!capture && signature !== null && signature === this.#lastDrawSignature)
      return;
    // Intermediate ticks replace the measured pose even without a GPU draw.
    // Retain only successfully completed frames, including their geometry.
    this.#lastDrawSignature = null;
    draw = capture || draw;
    if (draw) this.#renderer.info.reset();
    if (this.#stage)
      this.#stage.visible = isResults && !this.#captureBackground;
    if (this.#arena)
      for (const child of this.#arena.children)
        child.visible =
          !isResults || this.#captureBackground || child === this.#arena.snow;
    if (isMatch) {
      positionPlayer(this.#playerSnowman.root);
      for (const [side, sign] of [
        ["L", 1],
        ["R", -1],
      ] as const) {
        const mitten = this.#playerSnowman.root.getObjectByName(
          `anchor_mitten_${side}`,
        );
        // Gather extension from the 0.47 m shoulder is capped at 0.18 m.
        if (mitten)
          mitten.position.x =
            sign * (state.match.phase === "r1_gather" ? 0.47 + 0.18 : 0.7);
      }
      this.#opponentSnowman.root.position.set(0, 0, OPPONENT_Z);
      this.#opponentSnowman.root.rotation.set(0, 0, 0);
      this.#opponentSnowman.root.scale.setScalar(OPPONENT_SCALE);
    }

    this.#menuSnowball.visible = false;
    if (isMenu) {
      positionPlayer(this.#playerSnowman.root);
      this.#playerSnowman.root.rotation.y = 0;
      this.#playerSnowman.root.traverse((n) => {
        if (n.name.startsWith("face_")) n.visible = n.name === "face_grin";
      });
    }

    this.#playerSnowman.root.visible =
      isMenu || isMatch || (isResults && !this.#captureBackground);

    this.#opponentSnowman.root.visible =
      isMatch || (isResults && !this.#captureBackground);

    this.#projectiles.update(isMatch ? state.match : null, this.#camera);
    this.#hitParticles.update(
      isMatch ? state.match.hitEffects : [],
      isMatch ? state.match.tick : 100000,
      this.#camera,
      this.#reduceMotion,
    );

    if (isMatch && state.match.phase === "r2_roll") {
      this.#menuSnowball.visible = true;
      this.#menuSnowball.position.set(0, 0.35, PLAYER_Z - 0.65);
      this.#menuSnowball.rotation.x = time * 0.003;
      // Roll movement is centred on x=0 and cannot exceed 0.25 m.
      this.#playerSnowman.root.position.x = this.#reduceMotion
        ? 0
        : Math.sin(time * 0.003) * 0.25;
    }

    // Animate menu snowball
    if (isMenu) {
      this.#menuSnowball.rotation.x = time * 0.0002;
      this.#menuSnowball.rotation.y = time * 0.0005;
    }

    // Idle bob animation for snowmen during match
    if (isMatch) {
      const bob = this.#reduceMotion ? 0 : Math.sin(time * 0.002) * 0.05;

      this.#playerSnowman.root.position.y = bob;

      this.#opponentSnowman.root.position.y = bob + 0.05; // Slightly offset

      // Render projectiles
      if (state.match) {
        // Render hit effects (flash scale animation)

        state.match.hitEffects.forEach(
          (effect: { startTick: number; target: string }) => {
            const effectProgress =
              (state.match.tick - effect.startTick) / (0.3 * 60); // 0.3s duration at 60Hz

            const targetSnowman =
              effect.target === "player"
                ? this.#playerSnowman
                : this.#opponentSnowman;

            const age = effectProgress * 300;
            const pulse =
              this.#reduceMotion || age > 120
                ? 0
                : Math.sin((age / 120) * Math.PI);
            const base = effect.target === "player" ? 1 : OPPONENT_SCALE;
            targetSnowman.root.scale.set(
              base * (1 + 0.15 * pulse),
              base * (1 - 0.15 * pulse),
              base * (1 + 0.15 * pulse),
            );
          },
        );

        // Reset scale if no active hit effects

        if (state.match.hitEffects.length === 0) {
          this.#playerSnowman.root.scale.setScalar(1);

          this.#opponentSnowman.root.scale.setScalar(OPPONENT_SCALE);
        }
      }
    }

    // The world and UI render edge-to-edge; layout rects remain safe-area aware.
    this.#renderer.setScissorTest(false);
    if (draw) this.#renderer.clear();
    this.#renderer.setViewport(0, 0, this.#width, this.#height);
    const latestHit = isMatch
      ? state.match.hitEffects.filter((e) => e.target === "player").at(-1)
      : undefined;
    const elapsed =
      latestHit && isMatch
        ? ((state.match.tick - latestHit.startTick) * 1000) / 60
        : 180;
    const round =
      isMatch && /r2|roll/.test(state.match.phase)
        ? 2
        : isMatch && /r3|throw|sudden|resolve/.test(state.match.phase)
          ? 3
          : 1;
    const banner = isMatch && state.match.phase.startsWith("banner");
    const beat = cameraBeat(
      round,
      banner && !this.#reduceMotion
        ? ((state.match.tick - state.match.phaseStartTick) * 1000) / 60
        : 600,
    );
    const swayX = this.#reduceMotion ? 0 : 0.06 * Math.sin(time * 0.0006);
    const swayY = this.#reduceMotion ? 0 : 0.03 * Math.sin(time * 0.0009);
    const outgoing = isMatch
      ? state.match.projectiles.filter((p) => p.thrower === "player").at(-1)
      : undefined;
    const throwMs =
      outgoing && isMatch
        ? ((state.match.tick - outgoing.startTick) * 1000) / 60
        : 1000;
    const dolly = throwDolly(throwMs, this.#reduceMotion);
    this.#camera.position.set(
      beat.position[0]! + swayX,
      beat.position[1]! + swayY,
      beat.position[2]! - dolly,
    );
    this.#camera.lookAt(beat.target[0]!, beat.target[1]!, beat.target[2]!);
    this.#camera.fov =
      verticalFov(this.#width, this.#height) +
      (this.#reduceMotion ? 0 : 1.5 * Math.max(0, 1 - elapsed / 180) ** 2);
    this.#camera.updateProjectionMatrix();
    const offset = cameraShakeOffset(elapsed, this.#reduceMotion);
    this.#camera.setViewOffset(
      this.#width,
      this.#height,
      offset,
      offset * 0.5,
      this.#width,
      this.#height,
    );
    if (isMatch && state.match.phase === "r2_roll") {
      const root = this.#playerSnowman.root;
      const box = projectBounds(root, this.#camera, this.#width, this.#height);
      if (
        Math.abs(box.x + box.width / 2 - this.#width / 2) >
        this.#width * 0.04
      ) {
        const point = root.position.clone().project(this.#camera);
        point.x +=
          ((this.#width / 2 - box.x - box.width / 2) * 2) / this.#width;
        const centred = point.unproject(this.#camera);
        root.position.x = THREE.MathUtils.clamp(centred.x, -0.25, 0.25);
      }
    }
    if (
      isMatch &&
      !state.match.paused &&
      !this.#captureBackground &&
      ["r1_gather", "r2_roll", "r3_throw"].includes(state.match.phase)
    ) {
      const g = matchHudGeometry(
        this.#width,
        this.#height,
        Math.min(this.#layoutRects.dodgeArt.y, this.#layoutRects.blockArt.y),
        0,
        this.#layoutRects.mute.y + this.#layoutRects.mute.height,
      );
      const root = this.#playerSnowman.root;
      // Reproject after each move: perspective means a world-space translation
      // can also change the silhouette's size. Reserve the counter's pop width.
      for (let i = 0; i < 4; i++) {
        const box = projectBounds(
          root,
          this.#camera,
          this.#width,
          this.#height,
        );
        const point = root.position.clone().project(this.#camera);
        // v3: the player stays centred (C-CAM-5); the jar/tray/ammo pill sit
        // clear of the silhouette by layout, so no sideways nudge.
        const dx = 0;
        // Feet land just above the zone top (ZONE-BELOW-PLAYER) without lifting
        // the player further out of the C-CAM-2 band than needed.
        const dy = Math.min(0, g.zone.y - 0.25 - box.y - box.height);
        point.x += (dx * 2) / this.#width;
        point.y -= (dy * 2) / this.#height;
        root.position.copy(point.unproject(this.#camera));
      }
    }
    if (this.#captureBackground || isMenu) configureMatchCamera(this.#camera);
    if (isMenu) {
      const root = this.#playerSnowman.root;
      const box = projectBounds(root, this.#camera, this.#width, this.#height);
      const targetFeet = this.#layoutRects.play.y - this.#height * 0.06;
      const p = root.position.clone().project(this.#camera);
      p.y -= ((targetFeet - box.y - box.height) * 2) / this.#height;
      root.position.copy(p.unproject(this.#camera));
    }
    if (isResults && !this.#captureBackground) {
      const angle = this.#reduceMotion
        ? 0
        : (Math.sin((phaseTime / 6000) * Math.PI * 2) * Math.PI) / 30;
      // Tune the landscape viewing angle for a pixel-measured 22–24% oval.
      // Portrait and tablet retain their established camera.
      this.#camera.position.set(
        Math.sin(angle) * 5.2,
        this.#width > this.#height ? 1.57 : 2.1,
        2.4 + Math.cos(angle) * 5.2,
      );
      this.#camera.lookAt(0, 0.95, 2.4);
      this.#camera.fov = verticalFov(this.#width, this.#height);
      this.#camera.clearViewOffset();
      this.#camera.updateProjectionMatrix();
      const results = resultRects(this.#width, this.#height, this.#safe);
      if (this.#width > this.#height) {
        const center =
          (results.chips[0]!.x +
            results.chips[2]!.x +
            results.chips[2]!.width) /
          2;
        this.#camera.setViewOffset(
          this.#width,
          this.#height,
          this.#width / 2 - center,
          0,
          this.#width,
          this.#height,
        );
      }
      this.#stage?.update(
        state.outcome,
        phaseTime,
        this.#camera,
        this.#width,
        this.#height,
        this.#reduceMotion,
        results.subtitle.y +
          results.subtitle.height +
          (state.outcome === "draw" ? 8.2 : 12.2),
        results.chips[0]?.y ?? this.#height,
        this.#width > this.#height
          ? (results.chips[0]!.x +
              results.chips[2]!.x +
              results.chips[2]!.width) /
              2
          : this.#width / 2,
        this.#renderer.getPixelRatio(),
      );
    }
    if (ending?.ko && isMatch) {
      const m = koTimeline(ending.t, this.#reduceMotion);
      this.#camera.position.z -= m.dolly;
      this.#camera.fov -= m.zoom;
      this.#camera.updateProjectionMatrix();
      const loser =
        ending.loser === "player"
          ? this.#playerSnowman.root
          : this.#opponentSnowman.root;
      loser.rotation.z = m.fall;
      loser.traverse((n) => {
        if (n.name.startsWith("face_")) n.visible = n.name === "face_shocked";
      });
      this.#hitParticles.ko(
        ending.t,
        ending.loser,
        this.#camera,
        this.#reduceMotion,
      );
    }
    // Freeze the results backdrop at the last match render. The first results
    // render may be a QA seek; its UI timestamp must not move backdrop snow.
    this.#arena?.update(
      this.#captureBackground ? this.#lastMatchTime : time,
      this.#camera,
      this.#reduceMotion,
      this.#height,
    );
    if (draw) this.#renderer.render(this.#scene, this.#camera);
    else {
      // WebGLRenderer normally refreshes these even when poses were measured
      // earlier in the tick. Later poses and backdrop captures depend on them.
      if (this.#scene.matrixWorldAutoUpdate) this.#scene.updateMatrixWorld();
      if (this.#camera.parent === null && this.#camera.matrixWorldAutoUpdate)
        this.#camera.updateMatrixWorld();
    }
    if (this.#worldCaptures.length) {
      const png = this.#canvas.toDataURL("image/png");
      for (const resolve of this.#worldCaptures.splice(0)) resolve(png);
    }

    if (
      isResults &&
      this.#captureBackground &&
      typeof document !== "undefined"
    ) {
      const canvas = document.createElement("canvas");
      canvas.width = this.#width;
      canvas.height = this.#height;
      const c = canvas.getContext("2d");
      if (c) {
        c.filter = "blur(3.5px)";
        c.drawImage(this.#canvas, -8, -8, this.#width + 16, this.#height + 16);
        c.filter = "none";
        c.fillStyle =
          state.outcome === "win"
            ? "#3C46C845"
            : state.outcome === "lose"
              ? "#5A46A060"
              : "#4646966E";
        c.fillRect(0, 0, canvas.width, canvas.height);
        this.#resultBackground?.dispose();
        this.#resultBackground = new THREE.CanvasTexture(canvas);
        this.#resultBackground.colorSpace = THREE.SRGBColorSpace;
        this.#scene.background = this.#resultBackground;
      }
      this.#captureBackground = false;
    }

    if (!(isResults && this.#width > this.#height))
      this.#camera.clearViewOffset();
    this.#drawUi(state);
    this.#renderer.setViewport(0, 0, this.#width, this.#height);
    if (draw) this.#renderer.clearDepth();
    // Gameplay HUD uses its own canvas; an empty UI plane costs a draw call.
    if (draw && !this.#qaHidden && (isMenu || this.#debug))
      this.#renderer.render(this.#uiScene, this.#uiCamera);
    this.#drawCalls = this.#renderer.info.render.calls;
    this.#triangles = this.#renderer.info.render.triangles;
    if (draw && !capture) this.#lastDrawSignature = signature;
  }

  #drawUi(state: Readonly<GameState>): void {
    // Hidden QA geometry intentionally has no bounds (including home.logo).
    if (this.#qaHidden) return;
    const context = this.#uiContext;
    context.clearRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);
    context.save();
    context.scale(DESIGN_WIDTH / this.#width, DESIGN_HEIGHT / this.#height);
    context.textAlign = "center";
    context.textBaseline = "middle";

    if (state.kind === "menu") {
      const hit = this.#layoutRects.play;
      const padding = 8 * this.#viewport.scale;
      const play = {
        x: hit.x + padding,
        y: hit.y + padding,
        width: hit.width - padding * 2,
        height: hit.height - padding * 2,
      };
      const playX = (play.x - this.#viewport.x) / this.#viewport.scale;
      const playY = (play.y - this.#viewport.y) / this.#viewport.scale;
      const playWidth = play.width / this.#viewport.scale;
      const playHeight = play.height / this.#viewport.scale;

      const logo = this.getBounds("home.logo")!;
      drawArt(context, "logo", logo.x, logo.y, logo.width, logo.height);
      const coinLabel = String(state.profile?.coins ?? 0);
      context.font = `700 15px ${CANVAS_FONT_FAMILY}`;
      const coinWidth = 14 + 24 + 8 + context.measureText(coinLabel).width + 14;
      const coinX = this.#width - this.#safe.right - 12 - coinWidth,
        coinY = this.#safe.top + 10;
      this.#coinBounds = { x: coinX, y: coinY, width: coinWidth, height: 32 };
      context.fillStyle = "#FFFFFF";
      context.beginPath();
      context.roundRect(coinX, coinY, coinWidth, 32, 16);
      context.fill();
      drawArt(context, "icon_coin", coinX + 14, coinY + 4, 24, 24);
      context.fillStyle = "#2A2440";
      context.textAlign = "left";
      context.fillText(coinLabel, coinX + 14 + 24 + 8, coinY + 16);
      context.textAlign = "center";

      // PLAY button
      drawNineSlice(
        context,
        "btn_primary_normal",
        playX,
        playY,
        playWidth,
        playHeight,
        { left: 28, right: 28 },
      );
      context.fillStyle = theme.colors.ink;
      context.font = `700 28px ${CANVAS_FONT_FAMILY}`;
      context.fillText("PLAY", playX + playWidth / 2, playY + playHeight / 2);
    }

    if (this.#debug && state.kind === "match") {
      context.textAlign = "left";
      context.textBaseline = "top";
      // Debug panel: top-centre, below the gameplay HUD.
      const { x: debugX, y: debugY, width, height } = DEBUG_OVERLAY_DESIGN_RECT;
      context.fillStyle = "rgba(20, 26, 77, 0.88)";
      context.fillRect(debugX, debugY, width, height);
      context.fillStyle = "#ffffff";
      context.font = `600 14px ${CANVAS_FONT_FAMILY}`;
      context.fillText(
        `draw calls: ${this.#drawCalls} / 60`,
        debugX + 8,
        debugY + 26,
      );
      context.fillText(
        `triangles: ${this.#triangles} / 30000`,
        debugX + 8,
        debugY + 50,
      );
    }
    context.restore();
    this.#uiTexture.needsUpdate = true;
  }
}
